import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '../lib/AuthContext'
import { useLang } from '../lib/LangContext'
import { getBilanFinancier, getMouvementsPeriode, getProduits, getAnnulationsPeriode } from '../lib/api'
import jsPDF from 'jspdf'

// ═══════════════════════════════════════════════════════════
//  UTILITAIRES
// ═══════════════════════════════════════════════════════════

// FIX : ?? au lieu de || — un prix de 0 reste 0
const prixVenteDe = (m) => m.prix_unitaire ?? m.produits?.prix_unitaire ?? 0
const prixAchatDe = (m) => m.prix_achat    ?? m.produits?.prix_achat    ?? 0

function fmt(n) { return Math.round(n).toLocaleString('fr-FR') + ' FCFA' }

function fmtCourt(n) {
  if (Math.abs(n) >= 1000000) return (n / 1000000).toFixed(1) + 'M FCFA'
  if (Math.abs(n) >= 100000)  return Math.round(n / 1000) + 'k FCFA'
  return Math.round(n).toLocaleString('fr-FR') + ' FCFA'
}

// Formatage sans caractères spéciaux (jsPDF les affiche mal)
function N(n) {
  if (!n && n !== 0) return '-'
  if (n === 0) return '0'
  const s = Math.round(Math.abs(n)).toString()
  const parts = []
  for (let i = s.length; i > 0; i -= 3) parts.unshift(s.slice(Math.max(0, i - 3), i))
  return (n < 0 ? '-' : '') + parts.join(' ')
}

// ── FIX FUSEAU HORAIRE ──────────────────────────────────────
// Les dates sont stockées en UTC. On convertit vers l'heure locale
// pour que "aujourd'hui" corresponde vraiment à la journée du commerçant.
function cleJourLocale(dateStr) {
  const d = new Date(dateStr)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const j = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${j}`
}

function cleAujourdhui() {
  return cleJourLocale(new Date())
}

function estAujourdhui(dateStr) {
  return cleJourLocale(dateStr) === cleAujourdhui()
}

function getPeriodeDates(type) {
  const fin = new Date(); fin.setHours(23, 59, 59, 999)
  const debut = new Date(); debut.setHours(0, 0, 0, 0)

  if (type === 'hier') {
    // Journee complete d'hier : de 00h00 a 23h59
    debut.setDate(debut.getDate() - 1)
    fin.setDate(fin.getDate() - 1)
    fin.setHours(23, 59, 59, 999)
  } else if (type === 'semaine') {
    const jour = debut.getDay()
    debut.setDate(debut.getDate() - jour + (jour === 0 ? -6 : 1))
  } else if (type === 'mois') {
    debut.setDate(1)
  }
  return { debut, fin }
}

function groupParJour(mouvements) {
  const map = {}
  mouvements.forEach(m => {
    const jour = cleJourLocale(m.created_at)   // ← clé locale, pas UTC
    if (!map[jour]) map[jour] = { encaisse: 0, depense: 0, benefice: 0, achats: 0, mouvements: [] }
    const pv = prixVenteDe(m), pa = prixAchatDe(m)
    if (m.type === 'sortie') {
      map[jour].encaisse += m.quantite * pv
      map[jour].depense  += m.quantite * pa
      map[jour].benefice += m.quantite * (pv - pa)
    } else {
      map[jour].achats += m.quantite * pa
    }
    map[jour].mouvements.push(m)
  })
  return map
}

// ═══════════════════════════════════════════════════════════
//  GÉNÉRATION PDF
// ═══════════════════════════════════════════════════════════

function genererPDF(mouvements, boutiqueName, periodeLabel, lang, nbAnnules = 0) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const W = 210, margin = 12
  let y = 0

  const SAGE = [92,139,110], SAGE_L = [235,243,238]
  const CHARCOAL = [30,36,32], MUTED = [122,140,130]
  const CREAM = [247,244,239], ROSE = [184,92,92], ROSE_L = [250,234,234]
  const WHITE = [255,255,255]

  // En-tête
  doc.setFillColor(...SAGE); doc.rect(0, 0, W, 22, 'F')
  doc.setTextColor(...WHITE); doc.setFontSize(18); doc.setFont('helvetica','bold')
  doc.text('StockO', margin, 13)
  doc.setFontSize(9); doc.setFont('helvetica','normal')
  doc.text(lang === 'fr' ? 'Rapport Financier' : 'Financial Report', margin + 32, 13)
  doc.setFontSize(8)
  doc.text(boutiqueName, W - margin, 10, { align: 'right' })
  doc.text(new Date().toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric' }), W - margin, 17, { align: 'right' })
  y = 30

  doc.setTextColor(...MUTED); doc.setFontSize(8); doc.setFont('helvetica','italic')
  doc.text((lang === 'fr' ? 'Periode : ' : 'Period: ') + periodeLabel, margin, y)
  y += 4.5

  // Mention des mouvements neutralises : ils ne sont pas dans le tableau,
  // mais le commercant doit savoir qu'ils ont existe.
  if (nbAnnules > 0) {
    doc.setFontSize(7)
    doc.text(
      nbAnnules + (lang === 'fr'
        ? (nbAnnules > 1 ? ' mouvements annules — non comptes dans les totaux'
                         : ' mouvement annule — non compte dans les totaux')
        : (nbAnnules > 1 ? ' cancelled movements — excluded from totals'
                         : ' cancelled movement — excluded from totals')),
      margin, y)
    y += 4.5
  }
  y += 4.5

  const sorties = mouvements.filter(m => m.type === 'sortie')
  const totalEncaisse = sorties.reduce((s,m) => s + m.quantite * prixVenteDe(m), 0)
  const totalDepense  = sorties.reduce((s,m) => s + m.quantite * prixAchatDe(m), 0)
  const benefice = totalEncaisse - totalDepense

  const carteW = (W - margin*2 - 8) / 3
  const cartes = [
    { l: lang==='fr'?'Total encaisse':'Total received', v: totalEncaisse, c: SAGE, bg: SAGE_L },
    { l: lang==='fr'?'Cout des ventes':'Cost of sales', v: totalDepense,  c: ROSE, bg: ROSE_L },
    { l: lang==='fr'?'Benefice brut':'Gross profit',    v: benefice, c: benefice>=0?SAGE:ROSE, bg: benefice>=0?SAGE_L:ROSE_L },
  ]
  cartes.forEach((c, i) => {
    const x = margin + i * (carteW + 4)
    doc.setFillColor(...c.bg); doc.roundedRect(x, y, carteW, 18, 2, 2, 'F')
    doc.setDrawColor(...c.c); doc.setLineWidth(0.5); doc.roundedRect(x, y, carteW, 18, 2, 2, 'S')
    doc.setTextColor(...MUTED); doc.setFontSize(7); doc.setFont('helvetica','normal')
    doc.text(c.l, x + carteW/2, y + 6, { align:'center' })
    doc.setTextColor(...c.c); doc.setFontSize(11); doc.setFont('helvetica','bold')
    doc.text(((i===2 && c.v>=0)?'+':'') + N(c.v) + ' F', x + carteW/2, y + 14, { align:'center' })
  })
  y += 24

  doc.setTextColor(...CHARCOAL); doc.setFontSize(9); doc.setFont('helvetica','bold')
  doc.text(lang==='fr'?'Detail des mouvements':'Movement details', margin, y)
  doc.text(mouvements.length + (lang==='fr'?' operations':' operations'), W - margin, y, { align:'right' })
  y += 5

  const cols = [
    { l:'Date', w:22 }, { l:lang==='fr'?'Heure':'Time', w:14 },
    { l:lang==='fr'?'Produit':'Product', w:58 }, { l:'Type', w:16 },
    { l:lang==='fr'?'Qte':'Qty', w:12 }, { l:lang==='fr'?'Unite':'Unit', w:16 },
    { l:lang==='fr'?'P.Achat':'Buy', w:24 }, { l:lang==='fr'?'P.Vente':'Sell', w:24 },
  ]

  const dessinerEntete = () => {
    doc.setFillColor(...CHARCOAL); doc.rect(margin, y, W - margin*2, 7, 'F')
    doc.setTextColor(...WHITE); doc.setFontSize(7); doc.setFont('helvetica','bold')
    let x = margin + 1.5
    cols.forEach(c => { doc.text(c.l, x, y + 5); x += c.w })
    doc.text(lang==='fr'?'Valeur':'Value', W - margin - 2, y + 5, { align:'right' })
    y += 7
  }
  dessinerEntete()

  mouvements.forEach((m, idx) => {
    if (y > 265) { doc.addPage(); y = 12; dessinerEntete() }
    const dt = new Date(m.created_at)
    const pa = prixAchatDe(m), pv = prixVenteDe(m)
    const valeur = m.type === 'sortie' ? m.quantite * pv : m.quantite * pa
    const isEntree = m.type === 'entree'
    const H = 6.5

    if (idx % 2 === 0) { doc.setFillColor(...CREAM); doc.rect(margin, y, W - margin*2, H, 'F') }
    doc.setDrawColor(230,226,220); doc.setLineWidth(0.15)
    doc.line(margin, y + H, W - margin, y + H)

    const row = [
      dt.toLocaleDateString('fr-FR'),
      dt.toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' }),
      (m.produit_nom || '').length > 28 ? m.produit_nom.slice(0,28) + '...' : (m.produit_nom || ''),
      isEntree ? (lang==='fr'?'Entree':'Entry') : 'Sortie',
      String(m.quantite),
      m.produits?.unite || '',
      N(pa), N(pv),
    ]

    doc.setFontSize(7); let x = margin + 1.5
    row.forEach((v, ci) => {
      if (ci === 3) { doc.setTextColor(...(isEntree?SAGE:ROSE)); doc.setFont('helvetica','bold') }
      else { doc.setTextColor(...CHARCOAL); doc.setFont('helvetica','normal') }
      doc.text(v, x, y + 4.5); x += cols[ci].w
    })
    doc.setFont('helvetica','bold'); doc.setTextColor(...(isEntree?MUTED:SAGE))
    doc.text(N(valeur) + ' F', W - margin - 2, y + 4.5, { align:'right' })
    y += H
  })

  // Totaux
  y += 2
  if (y > 265) { doc.addPage(); y = 12 }
  doc.setFillColor(...SAGE); doc.rect(margin, y, W - margin*2, 8, 'F')
  doc.setTextColor(...WHITE); doc.setFontSize(8); doc.setFont('helvetica','bold')
  doc.text(lang==='fr'?'TOTAL VENTES':'TOTAL SALES', margin + 2, y + 5.5)
  doc.text(N(totalEncaisse) + ' F', W - margin - 2, y + 5.5, { align:'right' })
  y += 8
  doc.setFillColor(...(benefice>=0?SAGE_L:ROSE_L)); doc.rect(margin, y, W - margin*2, 7, 'F')
  doc.setTextColor(...(benefice>=0?SAGE:ROSE)); doc.setFontSize(7.5)
  doc.text(lang==='fr'?'BENEFICE BRUT':'GROSS PROFIT', margin + 2, y + 5)
  doc.text((benefice>=0?'+':'') + N(benefice) + ' F', W - margin - 2, y + 5, { align:'right' })

  // Pied de page
  const total = doc.getNumberOfPages()
  for (let i = 1; i <= total; i++) {
    doc.setPage(i)
    doc.setFillColor(...CREAM); doc.rect(0, 285, W, 12, 'F')
    doc.setDrawColor(...MUTED); doc.setLineWidth(0.2); doc.line(margin, 285, W - margin, 285)
    doc.setTextColor(...MUTED); doc.setFontSize(6.5); doc.setFont('helvetica','normal')
    doc.text('StockO — ' + boutiqueName, margin, 291)
    doc.text('Page ' + i + '/' + total, W/2, 291, { align:'center' })
    doc.text((lang==='fr'?'Genere le ':'Generated ') + new Date().toLocaleDateString('fr-FR'), W - margin, 291, { align:'right' })
  }

  return doc
}

// ═══════════════════════════════════════════════════════════
//  COMPOSANTS
// ═══════════════════════════════════════════════════════════

function MiniChart({ data, color, height = 36 }) {
  if (!data || data.length === 0) return null
  const max = Math.max(...data, 1)
  const w = 100 / data.length
  return (
    <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" style={{ width:'100%', height }}>
      {data.map((v, i) => {
        const h = (v / max) * (height - 4)
        return <rect key={i} x={i*w+0.5} y={height-h} width={w-1} height={h} rx="1.5" fill={color} opacity="0.85" />
      })}
    </svg>
  )
}

function CarteFinanciere({ icon, labelMois, labelJour, valeurMois, valeurJour, colorJour, bg, border, onClick, sousTitre }) {
  return (
    <div onClick={onClick} style={{
      background: bg, border: `1.5px solid ${border}`, borderRadius:'var(--radius-lg)',
      padding:'1rem 1.1rem', boxShadow:'var(--shadow-sm)',
      cursor: onClick ? 'pointer' : 'default', transition:'all 0.2s',
      position:'relative', userSelect:'none'
    }}>
      {onClick && <div style={{ position:'absolute', top:8, right:10, fontSize:12, color:'var(--muted)', opacity:0.5 }}>›</div>}
      <div style={{ fontSize:10, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 }}>
        {icon} {labelMois}
      </div>
      <div style={{ fontSize:13, color:'var(--muted)', marginBottom:6, lineHeight:1 }}>{fmtCourt(valeurMois)}</div>
      <div style={{ height:1, background:border, marginBottom:8, opacity:0.5 }} />
      <div style={{ fontSize:10, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 }}>
        📅 {labelJour}
      </div>
      <div style={{ fontFamily:'Fraunces, serif', fontSize:22, color:colorJour, lineHeight:1 }}>{valeurJour}</div>
      {sousTitre && <div style={{ fontSize:10, color:'var(--muted)', marginTop:4 }}>{sousTitre}</div>}
    </div>
  )
}

function ModalDetail({ type, joursMap, lang, onClose, totalMois, totalJour }) {
  const jours = Object.keys(joursMap).sort((a,b) => b.localeCompare(a))
  const titres = {
    encaisse: { fr:"Détail — J'ai encaissé", en:"Detail — I received" },
    depense:  { fr:"Détail — Coût des ventes", en:"Detail — Cost of sales" },
    benefice: { fr:"Détail — Mon bénéfice", en:"Detail — My profit" },
  }
  const color = type === 'benefice'
    ? (totalMois >= 0 ? 'var(--sage)' : 'var(--rose)')
    : type === 'encaisse' ? 'var(--sage)' : 'var(--rose)'
  const getVal = d => type === 'encaisse' ? d.encaisse : type === 'depense' ? d.depense : d.benefice

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxHeight:'85vh', overflowY:'auto', paddingBottom:'2rem' }}>
        <div className="modal-handle" />
        <div className="modal-title" style={{ marginBottom:'0.25rem' }}>
          {lang === 'fr' ? titres[type].fr : titres[type].en}
        </div>
        <div style={{ background:'var(--cream)', borderRadius:'var(--radius-md)', padding:'10px 14px',
          marginBottom:'1.25rem', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:12, color:'var(--muted)' }}>{lang==='fr'?'Total ce mois':'Total this month'}</div>
          <div style={{ fontFamily:'Fraunces, serif', fontSize:20, color }}>
            {type==='benefice' && totalMois>=0 ? '+' : ''}{fmt(totalMois)}
          </div>
        </div>

        {jours.length === 0 ? (
          <div className="empty">{lang==='fr'?'Aucune donnée':'No data'}</div>
        ) : jours.map(jour => {
          const data = joursMap[jour]
          const val = getVal(data)
          if (val === 0 && type !== 'benefice') return null
          const isToday = jour === cleAujourdhui()
          const displayVal = isToday ? totalJour : val
          const dateLabel = new Date(jour + 'T12:00:00').toLocaleDateString(
            lang==='fr'?'fr-FR':'en-GB', { weekday:'long', day:'numeric', month:'long' })
          return (
            <div key={jour} style={{ marginBottom:'1rem' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                padding:'8px 0', borderBottom:'1.5px solid var(--cream-dark)', marginBottom:8 }}>
                <div style={{ fontSize:12, fontWeight:600, color: isToday ? 'var(--sage)' : 'var(--charcoal)' }}>
                  {isToday ? (lang==='fr'?"Aujourd'hui":"Today") : dateLabel}
                </div>
                <div style={{ fontFamily:'Fraunces, serif', fontSize:16, color }}>
                  {type==='benefice' && displayVal>=0 ? '+' : ''}{fmt(displayVal)}
                </div>
              </div>
              {data.mouvements.filter(m => m.type === 'sortie').map(m => {
                const pa = prixAchatDe(m), pv = prixVenteDe(m)
                const v = type==='encaisse' ? m.quantite*pv
                  : type==='depense' ? m.quantite*pa
                  : m.quantite*(pv-pa)
                return (
                  <div key={m.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                    padding:'6px 8px', borderRadius:8, marginBottom:4, background:'var(--cream)' }}>
                    <div>
                      <div style={{ fontSize:12, fontWeight:500 }}>{m.produit_nom}</div>
                      <div style={{ fontSize:10, color:'var(--muted)' }}>
                        📤 {m.quantite} {m.produits?.unite || ''} · {new Date(m.created_at).toLocaleTimeString(lang==='fr'?'fr-FR':'en-GB', { hour:'2-digit', minute:'2-digit' })}
                      </div>
                    </div>
                    <div style={{ fontSize:13, fontWeight:600, color, textAlign:'right' }}>
                      {v>=0?'+':''}{fmt(v)}
                    </div>
                  </div>
                )
              })}
            </div>
          )
        })}
        <button className="btn-primary" onClick={onClose} style={{ marginTop:'1rem' }}>
          {lang==='fr'?'Fermer':'Close'}
        </button>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════
//  COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════

export default function Bilan() {
  const { boutiqueId, boutique } = useAuth()
  const { t, lang } = useLang()
  const [periode, setPeriode] = useState(30)
  const [mouvements, setMouvements] = useState([])
  const [produits, setProduits] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalType, setModalType] = useState(null)
  const [exportType, setExportType] = useState('jour')
  const [exporting, setExporting] = useState(false)
  const [exportMsg, setExportMsg] = useState('')

  const load = useCallback(async () => {
    if (!boutiqueId) return
    setLoading(true)
    const [{ data: movs }, { data: prods }] = await Promise.all([
      getBilanFinancier(boutiqueId, periode),
      getProduits(boutiqueId)
    ])
    setMouvements(movs || [])
    setProduits(prods || [])
    setLoading(false)
  }, [boutiqueId, periode])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') load() }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', load)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', load)
    }
  }, [load])

  // ── CALCULS ──
  // Double securite : on ignore toute annulation, meme si la requete
  // en laissait passer une (donnees anciennes, autre appel, etc.)
  const mouvementsValides = mouvements.filter(m => !m.annule && !m.est_annulation)
  const sorties = mouvementsValides.filter(m => m.type === 'sortie')

  const encaisseMois = sorties.reduce((s,m) => s + m.quantite * prixVenteDe(m), 0)
  const depenseMois  = sorties.reduce((s,m) => s + m.quantite * prixAchatDe(m), 0)
  const beneficeMois = encaisseMois - depenseMois

  // FIX : le stock se valorise au COÛT D'ACHAT, pas au prix de vente
  const stockAuCout  = produits.reduce((s,p) => s + p.quantite * (p.prix_achat ?? 0), 0)
  const stockAuPrixVente = produits.reduce((s,p) => s + p.quantite * (p.prix_unitaire ?? 0), 0)
  const margePotentielle = stockAuPrixVente - stockAuCout

  // FIX FUSEAU : filtre sur la clé de jour locale
  const sortiesJour  = sorties.filter(m => estAujourdhui(m.created_at))
  const encaisseJour = sortiesJour.reduce((s,m) => s + m.quantite * prixVenteDe(m), 0)
  const depenseJour  = sortiesJour.reduce((s,m) => s + m.quantite * prixAchatDe(m), 0)
  const beneficeJour = encaisseJour - depenseJour

  const joursMap = groupParJour(mouvementsValides)

  const derniers14 = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (13 - i))
    return cleJourLocale(d)
  })
  const dataEntrees = derniers14.map(j => joursMap[j]?.mouvements?.filter(m=>m.type==='entree').reduce((s,m)=>s+m.quantite,0) || 0)
  const dataSorties = derniers14.map(j => joursMap[j]?.mouvements?.filter(m=>m.type==='sortie').reduce((s,m)=>s+m.quantite,0) || 0)

  const topProduits = Object.entries(
    sorties.reduce((acc,m) => {
      const b = m.quantite * (prixVenteDe(m) - prixAchatDe(m))
      acc[m.produit_nom] = (acc[m.produit_nom] || 0) + b
      return acc
    }, {})
  ).sort((a,b) => b[1]-a[1]).slice(0,5)

  async function exporterPDF() {
    setExporting(true); setExportMsg('')
    try {
      const { debut, fin } = getPeriodeDates(exportType)
      const { data } = await getMouvementsPeriode(boutiqueId, debut, fin)
      if (!data || data.length === 0) {
        setExportMsg(lang==='fr'?'Aucun mouvement sur cette période.':'No movements for this period.')
        setExporting(false); return
      }
      const labels = {
        fr: { jour:"Aujourd'hui", hier:'Hier', semaine:'Cette semaine', mois:'Ce mois' },
        en: { jour:'Today', hier:'Yesterday', semaine:'This week', mois:'This month' }
      }
      const periodeLabel = `${labels[lang][exportType]} (${debut.toLocaleDateString(lang==='fr'?'fr-FR':'en-GB')} - ${fin.toLocaleDateString(lang==='fr'?'fr-FR':'en-GB')})`
      const { count: nbAnnules } = await getAnnulationsPeriode(boutiqueId, debut, fin)
      const doc = genererPDF(data, boutique?.nom || 'Boutique', periodeLabel, lang, nbAnnules)
      doc.save(`stocko_${(boutique?.nom||'boutique').replace(/\s/g,'_')}_${exportType}_${cleAujourdhui()}.pdf`)
      setExportMsg(lang==='fr'?'✓ PDF téléchargé !':'✓ PDF downloaded!')
    } catch (err) {
      console.error('PDF:', err)
      setExportMsg((lang==='fr'?'❌ Erreur : ':'❌ Error: ') + err.message)
    }
    setExporting(false)
  }

  const PERIODES = [
    { v:7,  l:t('days_7') },
    { v:30, l:t('days_30') },
    { v:90, l:t('months_3') },
  ]
  const EXPORT_OPTIONS = [
    { id:'jour',    icon:'📅', label: lang==='fr'?"Aujourd'hui":'Today' },
    { id:'hier',    icon:'⏪', label: lang==='fr'?'Hier':'Yesterday' },
    { id:'semaine', icon:'📆', label: lang==='fr'?'Cette semaine':'This week' },
    { id:'mois',    icon:'🗓️', label: lang==='fr'?'Ce mois':'This month' },
  ]
  const labelJour = lang==='fr' ? "Aujourd'hui" : "Today"

  return (
    <div className="main">
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.75rem' }}>
        <div className="page-title" style={{ marginBottom:0 }}>{t('financial_report')}</div>
        <button onClick={load} disabled={loading} style={{
          background:'var(--sage-pale)', border:'1px solid var(--sage-light)', borderRadius:50,
          padding:'5px 14px', cursor:'pointer', fontSize:12, color:'var(--sage-dark)',
          fontFamily:'DM Sans, sans-serif', fontWeight:500
        }}>{loading ? '⏳' : '🔄'}</button>
      </div>

      <div className="filter-tabs">
        {PERIODES.map(p => (
          <button key={p.v} className={`filter-tab ${periode===p.v?'active':''}`}
            onClick={() => setPeriode(p.v)}>{p.l}</button>
        ))}
      </div>

      {loading ? <div className="empty">{t('calculating')}</div> : (
        <>
          <div className="bilan-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:'1rem' }}>
            <CarteFinanciere
              icon="💰" labelMois={lang==='fr'?'Encaissé ce mois':'Received this month'}
              labelJour={labelJour} valeurMois={encaisseMois}
              valeurJour={fmtCourt(encaisseJour)}
              colorJour="var(--sage-dark)" bg="var(--sage-pale)" border="var(--sage-light)"
              onClick={() => setModalType('encaisse')}
            />
            <CarteFinanciere
              icon="🛒" labelMois={lang==='fr'?'Coût des ventes':'Cost of sales'}
              labelJour={labelJour} valeurMois={depenseMois}
              valeurJour={fmtCourt(depenseJour)}
              colorJour="var(--rose)" bg="var(--rose-pale)" border="#DFA0A0"
              onClick={() => setModalType('depense')}
            />
            {/* FIX : stock valorisé au coût d'achat */}
            <div style={{ background:'var(--amber-pale)', border:'1.5px solid #E8C080',
              borderRadius:'var(--radius-lg)', padding:'1rem 1.1rem', boxShadow:'var(--shadow-sm)' }}>
              <div style={{ fontSize:10, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 }}>
                📦 {lang==='fr'?'Mon stock vaut':'My stock is worth'}
              </div>
              <div style={{ fontFamily:'Fraunces, serif', fontSize:22, color:'var(--amber)', lineHeight:1, marginBottom:4 }}>
                {fmtCourt(stockAuCout)}
              </div>
              <div style={{ fontSize:10, color:'var(--muted)', lineHeight:1.5 }}>
                {lang==='fr'?"ce que tu as payé":'what you paid'}
              </div>
              <div style={{ height:1, background:'#E8C080', margin:'6px 0', opacity:0.5 }} />
              <div style={{ fontSize:10, color:'var(--muted)' }}>
                {lang==='fr'?'Marge si tout est vendu':'Margin if all sold'}
              </div>
              <div style={{ fontSize:13, color:'var(--sage-dark)', fontWeight:600 }}>
                +{fmtCourt(margePotentielle)}
              </div>
            </div>
            <CarteFinanciere
              icon={beneficeMois>=0?'📈':'📉'}
              labelMois={lang==='fr'?'Bénéfice ce mois':'Profit this month'}
              labelJour={labelJour} valeurMois={beneficeMois}
              valeurJour={(beneficeJour>=0?'+':'') + fmtCourt(beneficeJour)}
              colorJour={beneficeJour>=0?'var(--sage-dark)':'var(--rose)'}
              bg={beneficeMois>=0?'var(--sage-pale)':'var(--rose-pale)'}
              border={beneficeMois>=0?'var(--sage-light)':'#DFA0A0'}
              onClick={() => setModalType('benefice')}
            />
          </div>

          <div style={{ fontSize:11, color:'var(--muted)', textAlign:'center', marginBottom:'1rem' }}>
            {lang==='fr'?'💡 Clique sur une carte pour voir le détail jour par jour':'💡 Click a card for daily breakdown'}
          </div>

          <div style={{ background:'var(--warm-white)', border:'1px solid var(--cream-dark)',
            borderRadius:'var(--radius-lg)', padding:'1.1rem', marginBottom:'1rem', boxShadow:'var(--shadow-sm)' }}>
            <div style={{ fontSize:13, fontWeight:500, marginBottom:4 }}>{t('activity_chart')}</div>
            <div style={{ fontSize:11, color:'var(--muted)', marginBottom:10 }}>{t('movements_per_day')}</div>
            <div style={{ marginBottom:8 }}>
              <div style={{ fontSize:10, color:'var(--sage)', marginBottom:4, fontWeight:500 }}>↑ {t('entries')}</div>
              <MiniChart data={dataEntrees} color="#5C8B6E" />
            </div>
            <div>
              <div style={{ fontSize:10, color:'var(--rose)', marginBottom:4, fontWeight:500 }}>↓ {t('exits')}</div>
              <MiniChart data={dataSorties} color="#B85C5C" />
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:8 }}>
              <div style={{ fontSize:10, color:'var(--muted)' }}>
                {new Date(Date.now()-13*86400000).toLocaleDateString(lang==='fr'?'fr-FR':'en-GB', { day:'numeric', month:'short' })}
              </div>
              <div style={{ fontSize:10, color:'var(--muted)' }}>{t('today_label')}</div>
            </div>
          </div>

          {topProduits.length > 0 && (
            <div style={{ background:'var(--warm-white)', border:'1px solid var(--cream-dark)',
              borderRadius:'var(--radius-lg)', padding:'1.1rem', marginBottom:'1rem', boxShadow:'var(--shadow-sm)' }}>
              <div style={{ fontSize:13, fontWeight:500, marginBottom:'1rem' }}>
                🏆 {lang==='fr'?'Produits les plus rentables':'Most profitable products'}
              </div>
              {topProduits.map(([nom, benef], i) => (
                <div key={nom} style={{ marginBottom:12 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                    <div style={{ fontSize:12, fontWeight: i===0?600:400 }}>
                      {['🥇','🥈','🥉','4.','5.'][i]} {nom}
                    </div>
                    <div style={{ fontSize:12, color:'var(--sage)', fontWeight:500 }}>
                      +{Math.round(benef).toLocaleString('fr-FR')} FCFA
                    </div>
                  </div>
                  <div style={{ height:4, background:'var(--cream-dark)', borderRadius:2 }}>
                    <div style={{ width:`${Math.round((benef/topProduits[0][1])*100)}%`, height:'100%',
                      background:'var(--sage)', borderRadius:2, transition:'width 0.5s' }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ background:'var(--warm-white)', border:'1px solid var(--cream-dark)',
            borderRadius:'var(--radius-lg)', padding:'1.25rem', boxShadow:'var(--shadow-sm)' }}>
            <div style={{ fontFamily:'Fraunces, serif', fontSize:16, marginBottom:4 }}>
              📄 {lang==='fr'?'Export PDF':'PDF Export'}
            </div>
            <div style={{ fontSize:12, color:'var(--muted)', marginBottom:'1.25rem', lineHeight:1.6 }}>
              {lang==='fr'
                ? 'Rapport avec résumé financier et détail des mouvements.'
                : 'Report with financial summary and movement details.'}
            </div>
            <div className="export-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:'1.25rem' }}>
              {EXPORT_OPTIONS.map(opt => (
                <button key={opt.id} onClick={() => setExportType(opt.id)} style={{
                  padding:'12px 8px', borderRadius:'var(--radius-md)', cursor:'pointer', textAlign:'center',
                  border:`2px solid ${exportType===opt.id?'var(--sage)':'var(--cream-dark)'}`,
                  background: exportType===opt.id?'var(--sage-pale)':'var(--cream)',
                  fontFamily:'DM Sans, sans-serif', transition:'all 0.15s'
                }}>
                  <div style={{ fontSize:22, marginBottom:4 }}>{opt.icon}</div>
                  <div style={{ fontSize:11, fontWeight:600, color: exportType===opt.id?'var(--sage-dark)':'var(--charcoal)' }}>
                    {opt.label}
                  </div>
                </button>
              ))}
            </div>
            {exportMsg && (
              <div className={exportMsg.startsWith('✓')?'success-msg':'error-msg'} style={{ marginBottom:10 }}>
                {exportMsg}
              </div>
            )}
            <button className="btn-primary" onClick={exporterPDF} disabled={exporting}>
              {exporting
                ? (lang==='fr'?'Génération...':'Generating...')
                : `📄 ${lang==='fr'?'Télécharger':'Download'} — ${EXPORT_OPTIONS.find(o=>o.id===exportType)?.label}`}
            </button>
          </div>
        </>
      )}

      {modalType && (
        <ModalDetail
          type={modalType} joursMap={joursMap} lang={lang}
          onClose={() => setModalType(null)}
          totalMois={modalType==='encaisse'?encaisseMois:modalType==='depense'?depenseMois:beneficeMois}
          totalJour={modalType==='encaisse'?encaisseJour:modalType==='depense'?depenseJour:beneficeJour}
        />
      )}
    </div>
  )
}
